import axios from 'axios';
import { API_BASE_URL, apiUrl } from './apiBaseUrl.js';
import { withStoreHeaders } from './storeHeaders.js';
import { isDefinitiveAuthFailure } from '../Utils/authErrors.js';
import { clearAuthToken, getAuthToken } from '../Utils/authToken.js';
import { handlePaymentRequiredResponse } from '../Utils/billingPaymentRequired.js';
import { getUiLanguage } from '../Utils/uiLocale.js';

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30s timeout: tolerates serverless cold starts (e.g. Vercel + MongoDB reconnect)
    headers: {
        'Content-Type': 'application/json',
    },
});


// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        const storeHeaders = withStoreHeaders();
        if (storeHeaders['X-Store-Id']) {
            config.headers['X-Store-Id'] = storeHeaders['X-Store-Id'];
        }
        config.headers['X-App-Language'] = getUiLanguage();
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Check if it's a 401 error (unauthorized)
        if (error.response?.status === 401) {
            // Don't redirect if this is the login endpoint
            if (error.config.url === '/api/admin/login') {
                return Promise.reject(error);
            }

            if (isDefinitiveAuthFailure(error)) {
                // Clear tokens
                clearAuthToken();
                sessionStorage.setItem('aaa_dashboard_session_expired', '1');

                window.location.href = '/session-expired';
            }
        }

        if (error.response?.status === 403 && error.response?.data?.passwordChangeRequired) {
            const path = window.location.pathname || '';
            if (path !== '/password-required') {
                window.location.href = '/password-required';
                return Promise.reject(error);
            }
        }

        handlePaymentRequiredResponse(error);

        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        handlePaymentRequiredResponse(error);
        return Promise.reject(error);
    },
);

// ==================== AUTHENTICATION APIS ====================

export const authAPI = {
    // Register a new admin
    register: (userData) => api.post('/api/admin/register', userData),

    // Login
    login: (email, password) => api.post('/api/admin/login', { email, password }),

    // Verify 2FA
    verify2FA: (code, tempToken, useBackupCode = false) => api.post('/api/admin/verify-2fa', { code, tempToken, useBackupCode }),

    // Logout
    logout: () => api.post('/api/admin/logout'),

    // Request password reset
    requestPasswordReset: (email) => api.post('/api/admin/request-password-reset', { email }),

    // Reset password
    resetPassword: (token, password, confirmPassword) => api.post('/api/admin/reset-password', { token, password, confirmPassword }),

    /** Public: confirm email from link (no auth required) */
    verifyEmail: (token, id) => api.post('/api/admin/verify-email', { token, id }),

    /** Public: confirm email with 6-digit code (no auth required) */
    verifyEmailCode: (email, code) => api.post('/api/admin/verify-email-code', { email, code }),

    /** Public: resend email verification code */
    resendVerificationCode: (email) => api.post('/api/admin/resend-verification-code', { email }),

    // intent: 'login' only signs in existing merchants; 'register' only creates new ones.
    googleAuth: ({ intent = 'login', plan, interval } = {}) => {
        const params = new URLSearchParams({
            origin: window.location.origin,
            intent,
            lang: getUiLanguage(),
        });
        if (plan) {
            params.set('plan', plan);
        }
        if (interval) {
            params.set('interval', interval);
        }
        return apiUrl(`/api/admin/google?${params.toString()}`);
    },

    // Change password
    changePassword: (currentPassword, newPassword, confirmPassword) =>
        api.post('/api/admin/change-password', { currentPassword, newPassword, confirmPassword }),

    // Google-only accounts (no password yet): email a one-time code, then set the first password
    requestPasswordSetupCode: () => api.post('/api/admin/password/setup-code'),
    setupPassword: (code, newPassword, confirmPassword) =>
        api.post('/api/admin/password/setup', { code, newPassword, confirmPassword }),
};

// ==================== PROFILE APIS ====================
export const profileAPI = {
    // Get current user profile
    getProfile: () => api.get('/api/admin/profile'),

    // Update profile
    updateProfile: (profileData) => api.put('/api/admin/profile', profileData),

    /** Send verification link to current admin email */
    requestEmailVerification: () => api.post('/api/admin/request-email-verification'),

    // Update avatar
    updateAvatar: (data) => {
        // Check if it's FormData (file upload) or plain object (URL)
        if (data instanceof FormData) {
            return api.put('/api/admin/profile/avatar', data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        } else {
            // For URL submission, send as JSON
            return api.put('/api/admin/profile/avatar', data, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
        }
    },
};

// ==================== TWO-FACTOR AUTHENTICATION APIS ====================

export const twoFactorAPI = {
    // Setup 2FA (get QR code)
    setup2FA: () => api.get('/api/admin/2fa/setup'),

    // Enable 2FA
    enable2FA: (code) => api.post('/api/admin/2fa/enable', { code }),

    // Disable 2FA
    disable2FA: (password, code) => api.post('/api/admin/2fa/disable', { password, code }),
};

// ==================== SESSION MANAGEMENT APIS ====================

export const sessionAPI = {
    // Get active sessions
    getActiveSessions: () => api.get('/api/admin/sessions'),

    // Clear all sessions
    clearAllSessions: () => api.delete('/api/admin/sessions'),
};

// ==================== API KEY MANAGEMENT APIS ====================

export const apiKeyAPI = {
    // Generate API key (one key per admin; backend ignores name/permissions/expiry)
    generateApiKey: () => api.post('/api/admin/api-key/generate'),

    // Revoke API key
    revokeApiKey: () => api.delete('/api/admin/api-key/revoke'),
};

// ==================== ACTIVITY LOGS APIS ====================

export const activityLogsAPI = {
    // Get current user's activity logs
    getActivityLogs: (params) => api.get('/api/admin/activity-logs', { params }),

    // Get specific admin's activity logs (requires permission)
    getAdminActivityLogs: (adminId, params) =>
        api.get(`/api/admin/admins/${adminId}/activity-logs`, { params }),
};

// ==================== ADMIN MANAGEMENT APIS ====================

export const adminAPI = {
    // Register a new admin (requires manage_admins)
    registerAdmin: (adminData) => {
        if (adminData instanceof FormData) {
            return api.post('/api/admin/admins/register', adminData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        }
        return api.post('/api/admin/admins/register', adminData);
    },

    // Get all admins
    getAllAdmins: (params) => api.get('/api/admin/admins', { params }),

    // Get admin by ID
    getAdminById: (id) => api.get(`/api/admin/admins/${id}`),

    // Update admin
    updateAdmin: (id, adminData) => api.put(`/api/admin/admins/${id}`, adminData),

    // Delete admin
    deleteAdmin: (id) => api.delete(`/api/admin/admins/${id}`),

    // Reset admin password directly
    resetAdminPassword: (id, password, confirmPassword) =>
        api.post(`/api/admin/admins/${id}/reset-password`, { password, confirmPassword }),

    // Toggle admin 2FA
    toggleAdmin2FA: (id, enabled) => api.post(`/api/admin/admins/${id}/2fa`, { enabled }),

    // Suspend admin
    suspendAdmin: (id, { reason, suspendedUntil } = {}) =>
        api.post(`/api/admin/admins/${id}/suspend`, {
            ...(reason ? { reason } : {}),
            ...(suspendedUntil ? { suspendedUntil } : {}),
        }),

    // Activate admin
    activateAdmin: (id) => api.post(`/api/admin/admins/${id}/activate`),

    // Transfer organization ownership to another admin
    transferOwnership: (id) => api.post(`/api/admin/admins/${id}/transfer-ownership`),

    // Add permissions to admin (super_admin only)
    addPermissions: (id, permissions) => api.post(`/api/admin/admins/${id}/permissions`, { permissions }),

    // Get admin activity logs
    getAdminActivityLogs: (id) => api.get(`/api/admin/admins/${id}/activity-logs`),
};

// ==================== NOTIFICATION APIS ====================

export const notificationAPI = {
    // Get notifications
    getNotifications: (params) => api.get('/api/admin/notifications', { params }),

    // Create notification
    createNotification: (data) => api.post('/api/admin/notifications', data),

    // Mark as read
    markAsRead: (id) => api.put(`/api/admin/notifications/${id}/read`),

    // Delete notification
    deleteNotification: (id) => api.delete(`/api/admin/notifications/${id}`),
};

// Export the configured axios instance so other modules can use it (with interceptors)
export default api;